<script setup>
// Editor visual de e-mail (v2) em três colunas: adicionar | e-mail | propriedades/IA.
// O composable vai por provide para canvas e painéis.
import { ref, computed, provide, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useEmailDocument } from './useEmailDocument';
import EmailCanvas from './canvas/EmailCanvas.vue';
import AddPanel from './panels/AddPanel.vue';
import PropertiesPanel from './panels/PropertiesPanel.vue';
import EmailAiChat from './EmailAiChat.vue';
import RealPreviewDialog from './RealPreviewDialog.vue';
import SaveBlockDialog from './SaveBlockDialog.vue';
import PublishGalleryButton from './PublishGalleryButton.vue';
import SendTestDialog from './SendTestDialog.vue';

const props = defineProps({
  templateId: { type: [String, Number], required: true },
});
const emit = defineEmits(['modeChanged']);
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const ed = useEmailDocument({
  templateId: props.templateId,
  onError: error =>
    useAlert(
      error?.response?.data?.error || t('EMAIL_TEMPLATES.EDITOR.SAVE_RETRY')
    ),
  onLimit: () => useAlert(t('EMAIL_TEMPLATES.EDITOR.LIMIT_REACHED')),
});
provide('emailEditor', ed);
const {
  template,
  doc,
  loading,
  device,
  saveState,
  converted,
  canUndo,
  canRedo,
  composing,
} = ed;

const rightTab = ref('props');
const preview = ref(null);
const testDialog = ref(null);
const stateLabel = computed(() =>
  t(`EMAIL_TEMPLATES.BUILDER.SAVE_STATE.${saveState.value}`)
);
const previewSource = () => ({
  mode: 'newsletter',
  layout: doc.value,
  settings: template.value?.settings || {},
});

const goBack = async () => {
  await ed.flush();
  router.push({
    name: 'email_templates_index',
    params: { accountId: route.params.accountId },
  });
};
const changeMode = async mode => {
  const previous = template.value.mode;
  ed.updateTemplate({ mode });
  await ed.flush();
  if (saveState.value === 'saved') {
    emit('modeChanged');
    return;
  }
  ed.updateTemplate({ mode: previous });
  useAlert(t('EMAIL_TEMPLATES.SAVE_ERROR'));
};

const isTyping = target =>
  ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName) ||
  target?.isContentEditable;
const dialogOpen = event =>
  !!document.querySelector('dialog[open], [role="dialog"]') ||
  !!event.target?.closest?.('dialog, [role="dialog"]');
const hasTextSelection = () => !!window.getSelection?.()?.toString();
const onKey = event => {
  if (dialogOpen(event)) return;
  const mod = event.ctrlKey || event.metaKey;
  const key = event.key.toLowerCase();
  if (mod && key === 's') {
    event.preventDefault();
    ed.save();
    return;
  }
  // Montagem paga em andamento: nenhum atalho mexe no e-mail (nem desfazer).
  if (composing?.value || isTyping(event.target)) return;
  const id = ed.selectedId.value;
  if (mod && key === 'z') {
    event.preventDefault();
    if (event.shiftKey) ed.redo();
    else ed.undo();
  } else if (mod && key === 'y') {
    event.preventDefault();
    ed.redo();
  } else if (mod && key === 'd' && id) {
    event.preventDefault();
    ed.duplicate(id);
  } else if (mod && key === 'c' && id && !hasTextSelection()) {
    ed.copy(id);
  } else if (mod && key === 'v' && !hasTextSelection()) {
    ed.paste();
  } else if (
    (key === 'delete' || key === 'backspace') &&
    id &&
    !mod &&
    !event.altKey
  ) {
    event.preventDefault();
    ed.remove(id);
  } else if (key === 'escape') {
    ed.selectParent();
  } else if (
    (key === 'arrowup' || key === 'arrowdown') &&
    ed.selected.value?.kind === 'element'
  ) {
    event.preventDefault();
    const { column, elIndex } = ed.selected.value;
    const next = column.elements[elIndex + (key === 'arrowup' ? -1 : 1)];
    if (next) ed.select(next.id);
  }
};
const onBeforeUnload = event => {
  if (['dirty', 'saving', 'error'].includes(saveState.value)) {
    event.preventDefault();
    event.returnValue = t('EMAIL_TEMPLATES.EDITOR.LEAVE_CONFIRM'); // eslint-disable-line no-param-reassign
  }
};

onMounted(() => {
  ed.load();
  window.addEventListener('keydown', onKey);
  window.addEventListener('beforeunload', onBeforeUnload);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey);
  window.removeEventListener('beforeunload', onBeforeUnload);
  ed.flush();
});
const inputClass =
  'rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand';
const ghostBtn =
  'rounded-lg border border-n-weak px-2.5 py-1.5 text-sm text-n-slate-11 hover:bg-n-solid-2';
</script>

<template>
  <div class="flex h-full w-full flex-col bg-n-background">
    <header
      class="flex items-center gap-2 border-b border-n-weak bg-n-solid-1 px-3 py-2"
    >
      <button
        type="button"
        class="flex shrink-0 items-center gap-1 text-sm text-n-slate-11 hover:text-n-slate-12"
        @click="goBack"
      >
        <span class="i-lucide-arrow-left" /> {{ t('EMAIL_TEMPLATES.BACK') }}
      </button>
      <template v-if="template">
        <input
          :value="template.name"
          :placeholder="t('EMAIL_TEMPLATES.NAME')"
          :class="inputClass"
          class="w-48 shrink-0"
          @input="ed.updateTemplate({ name: $event.target.value })"
        />
        <input
          :value="template.subject"
          :placeholder="t('EMAIL_TEMPLATES.SUBJECT')"
          :class="inputClass"
          class="min-w-0 flex-1"
          @input="ed.updateTemplate({ subject: $event.target.value })"
        />
        <select
          :value="template.mode"
          :class="inputClass"
          class="!w-36 shrink-0 disabled:opacity-50"
          data-test="mode"
          :disabled="composing"
          @change="changeMode($event.target.value)"
        >
          <option value="newsletter">
            {{ t('EMAIL_TEMPLATES.BUILDER.MODE.newsletter') }}
          </option>
          <option value="personal">
            {{ t('EMAIL_TEMPLATES.BUILDER.MODE.personal') }}
          </option>
        </select>
        <div class="flex items-center gap-1">
          <button
            type="button"
            class="i-lucide-undo-2 text-n-slate-11 disabled:opacity-30"
            :disabled="!canUndo || composing"
            :title="t('EMAIL_TEMPLATES.BUILDER.ACTIONS.UNDO')"
            @click="ed.undo()"
          />
          <button
            type="button"
            class="i-lucide-redo-2 text-n-slate-11 disabled:opacity-30"
            :disabled="!canRedo || composing"
            :title="t('EMAIL_TEMPLATES.BUILDER.ACTIONS.REDO')"
            @click="ed.redo()"
          />
        </div>
        <div class="flex gap-0.5 rounded-lg border border-n-weak p-0.5">
          <button
            v-for="d in ['desktop', 'mobile']"
            :key="d"
            type="button"
            class="rounded-md px-2 py-1"
            :class="
              device === d ? 'bg-n-brand/10 text-n-brand' : 'text-n-slate-11'
            "
            :title="t(`EMAIL_TEMPLATES.EDITOR.TOPBAR.${d.toUpperCase()}`)"
            @click="device = d"
          >
            <span
              :class="
                d === 'desktop' ? 'i-lucide-monitor' : 'i-lucide-smartphone'
              "
              class="block size-4"
            />
          </button>
        </div>
        <PublishGalleryButton
          :template-id="templateId"
          :template-name="template.name"
          :source="previewSource"
          :before-publish="ed.flush"
        />
        <button type="button" :class="ghostBtn" @click="preview?.open()">
          {{ t('EMAIL_TEMPLATES.EDITOR.TOPBAR.REAL_PREVIEW') }}
        </button>
        <button type="button" :class="ghostBtn" @click="testDialog?.open()">
          {{ t('EMAIL_TEMPLATES.BUILDER.ACTIONS.TEST') }}
        </button>
        <span
          class="w-20 text-right text-[11px]"
          :class="saveState === 'error' ? 'text-n-ruby-9' : 'text-n-slate-10'"
        >
          {{ stateLabel }}
        </span>
        <button
          type="button"
          class="rounded-lg bg-n-brand px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          :disabled="saveState === 'saving'"
          @click="ed.save()"
        >
          {{ t('EMAIL_TEMPLATES.SAVE') }}
        </button>
      </template>
    </header>
    <p
      v-if="converted"
      class="border-b border-n-amber-6 bg-n-amber-2 px-4 py-1.5 text-xs text-n-amber-11"
    >
      {{ t('EMAIL_TEMPLATES.EDITOR.CONVERTED') }}
    </p>

    <div
      v-if="loading || !doc"
      class="flex flex-1 items-center justify-center text-sm text-n-slate-11"
    >
      {{ t('EMAIL_TEMPLATES.LOADING') }}
    </div>
    <div v-else class="flex min-h-0 flex-1">
      <div class="relative flex min-w-0 flex-1">
        <aside class="w-64 shrink-0 border-r border-n-weak bg-n-solid-1">
          <AddPanel />
        </aside>
        <main class="min-w-0 flex-1 overflow-auto"><EmailCanvas /></main>
        <div
          v-if="composing"
          data-test="composing"
          class="absolute inset-0 z-20 flex items-center justify-center bg-n-background/70"
        >
          <span
            class="flex items-center gap-2 rounded-lg bg-n-solid-1 px-4 py-2 text-sm text-n-slate-12 shadow"
          >
            <span class="i-lucide-loader-2 animate-spin" />
            {{ t('EMAIL_TEMPLATES.EDITOR.CHAT.COMPOSING') }}
          </span>
        </div>
      </div>
      <aside
        class="flex w-80 shrink-0 flex-col border-l border-n-weak bg-n-solid-1"
      >
        <div class="flex border-b border-n-weak">
          <button
            v-for="tab in ['props', 'ai']"
            :key="tab"
            type="button"
            class="flex-1 border-b-2 py-2 text-xs font-semibold"
            :class="
              rightTab === tab
                ? 'border-n-brand text-n-brand'
                : 'border-transparent text-n-slate-10'
            "
            @click="rightTab = tab"
          >
            {{
              tab === 'ai'
                ? t('EMAIL_TEMPLATES.EDITOR.TOPBAR.AI')
                : t('EMAIL_TEMPLATES.EDITOR.TOPBAR.PROPERTIES')
            }}
          </button>
        </div>
        <div class="min-h-0 flex-1">
          <div
            v-if="rightTab === 'props'"
            data-test="props-wrapper"
            class="h-full"
            :class="composing && 'pointer-events-none opacity-50'"
            :inert="composing || undefined"
          >
            <PropertiesPanel />
          </div>
          <EmailAiChat v-show="rightTab !== 'props'" class="h-full" />
        </div>
      </aside>
    </div>
    <RealPreviewDialog ref="preview" :source="previewSource" />
    <SaveBlockDialog />
    <SendTestDialog
      ref="testDialog"
      :template-id="templateId"
      :before-send="ed.flush"
    />
  </div>
</template>
